import { useCallback, useEffect, useRef, useState } from 'react';
import {
  SCHEME_FONT_SIZE_DEFAULT,
  clampSchemeZoomPercent,
  getSchemeZoomPercentBounds,
} from '../utils/schemeZoom';

const SCRUB_PX_PER_PERCENT = 3;

export type SchemeZoomGestureBefore = { percent: number };

interface SchemeZoomControlsProps {
  zoomPercent: number;
  onZoomPercentChange: (percent: number) => void;
  onZoomSettled: (percentCommitted: number, before: SchemeZoomGestureBefore) => void;
}

export function SchemeZoomControls({
  zoomPercent,
  onZoomPercentChange,
  onZoomSettled,
}: SchemeZoomControlsProps) {
  const scrubRef = useRef<{
    pointerId: number;
    startY: number;
    startPercent: number;
  } | null>(null);
  const scrubStartPercentRef = useRef(0);
  const scrubPercentEndRef = useRef(0);
  const scrubGestureBeforeRef = useRef<SchemeZoomGestureBefore>({
    percent: 100,
  });

  const [percentDraft, setPercentDraft] = useState(String(zoomPercent));
  const [isEditingPercent, setIsEditingPercent] = useState(false);

  useEffect(() => {
    if (isEditingPercent) return;
    const next = String(zoomPercent);
    setPercentDraft((prev) => (prev === next ? prev : next));
  }, [zoomPercent, isEditingPercent]);

  const { min: pctMin, max: pctMax } = getSchemeZoomPercentBounds(SCHEME_FONT_SIZE_DEFAULT);

  const commitPercentDraft = useCallback(() => {
    const trimmed = percentDraft.trim();
    if (trimmed === '') {
      setPercentDraft(String(zoomPercent));
      return;
    }
    const n = Number.parseInt(trimmed, 10);
    if (!Number.isFinite(n)) {
      setPercentDraft(String(zoomPercent));
      return;
    }
    const clamped = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, n);
    if (clamped === zoomPercent) {
      setPercentDraft(String(zoomPercent));
      return;
    }
    onZoomSettled(clamped, { percent: zoomPercent });
  }, [percentDraft, onZoomSettled, zoomPercent]);

  const handlePercentInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setIsEditingPercent(true);
    const digits = e.target.value.replace(/\D/g, '');
    setPercentDraft(digits);
  }, []);

  const handlePercentKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        commitPercentDraft();
        setIsEditingPercent(false);
        (e.currentTarget as HTMLInputElement).blur();
      }
    },
    [commitPercentDraft],
  );

  const endScrub = useCallback(
    (e: React.PointerEvent) => {
      const s = scrubRef.current;
      if (!s || e.pointerId !== s.pointerId) return;
      try {
        (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
      } catch {
        /* ignore */
      }

      scrubRef.current = null;
    },
    [],
  );

  const handleScrubPointerDown = useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
      scrubGestureBeforeRef.current = { percent: zoomPercent };
      scrubStartPercentRef.current = zoomPercent;
      scrubPercentEndRef.current = zoomPercent;
      scrubRef.current = {
        pointerId: e.pointerId,
        startY: e.clientY,
        startPercent: zoomPercent,
      };
    },
    [zoomPercent],
  );

  const handleScrubPointerMove = useCallback(
    (e: React.PointerEvent) => {
      const s = scrubRef.current;
      if (!s || e.pointerId !== s.pointerId) return;
      const dy = e.clientY - s.startY;
      const delta = Math.round(-dy / SCRUB_PX_PER_PERCENT);
      const next = clampSchemeZoomPercent(SCHEME_FONT_SIZE_DEFAULT, s.startPercent + delta);
      scrubPercentEndRef.current = next;
      onZoomPercentChange(next);
    },
    [onZoomPercentChange],
  );

  return (
    <div className="build-scheme-zoom-fill-row">
      <div className="build-scheme-zoom-percent-wrap">
        <label className="build-size-label" htmlFor="scheme-zoom-percent-input">
          Zoom
        </label>
        <div className="build-scheme-zoom-percent-control">
          <input
            id="scheme-zoom-percent-input"
            type="text"
            inputMode="numeric"
            pattern="[0-9]*"
            className="build-scheme-zoom-percent-input"
            value={percentDraft}
            onFocus={() => setIsEditingPercent(true)}
            onChange={handlePercentInputChange}
            onBlur={() => {
              setIsEditingPercent(false);
              commitPercentDraft();
            }}
            onKeyDown={handlePercentKeyDown}
            aria-valuemin={pctMin}
            aria-valuemax={pctMax}
            aria-label="Zoom percent"
          />
          <button
            type="button"
            className="build-scheme-zoom-percent-scrub"
            aria-label="Drag vertically to adjust zoom percent"
            onPointerDown={handleScrubPointerDown}
            onPointerMove={handleScrubPointerMove}
            onPointerUp={endScrub}
            onPointerCancel={endScrub}
          >
            <span className="build-scheme-zoom-percent-suffix" aria-hidden="true">
              %
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
